import type { ColumnsType } from "antd/es/table";

import { AppStrings } from "../../utils/constants/AppStrings";
import { formatCurrency } from "../../utils/financialFormatters";
import { formatDecimal } from "./reportingSummaryUtils";
import {
  ReportingSummaryMarginCell,
  ReportingSummaryPlCell,
} from "./ReportingSummaryTableCells";
import type {
  ProfitabilityEventMetricsRow,
  ProfitabilitySportMetricsRow,
  ProfitabilitySummaryMetricsRow,
} from "./reportingSummaryTypes";

export function getProfitabilitySummaryColumns(
  currencySymbol: string = "$",
): ColumnsType<ProfitabilitySummaryMetricsRow> {
  return [
    {
      title: AppStrings.ACCEPTED_BETS,
      dataIndex: "acceptedBets",
      key: "acceptedBets",
      align: "right",
      render: (v: number) => formatDecimal(v, 0),
    },
    {
      title: AppStrings.REPORTING_ACCEPTED_TURNOVER,
      dataIndex: "acceptedTurnover",
      key: "acceptedTurnover",
      align: "right",
      render: (v: number) => formatCurrency(v, currencySymbol, 2),
    },
    {
      title: AppStrings.REPORTING_AVG_STAKE,
      dataIndex: "avgStake",
      key: "avgStake",
      align: "right",
      render: (v: number) => formatCurrency(v, currencySymbol, 2),
    },
    {
      title: AppStrings.REPORTING_PL_COLUMN,
      dataIndex: "pl",
      key: "pl",
      align: "right",
      render: (v: number) => (
        <ReportingSummaryPlCell value={v} currencySymbol={currencySymbol} />
      ),
    },
    {
      title: AppStrings.MARGIN,
      dataIndex: "margin",
      key: "margin",
      align: "right",
      render: (v: number) => <ReportingSummaryMarginCell value={v} />,
    },
  ];
}

export function getProfitabilitySportsColumns(
  currencySymbol: string = "$",
): ColumnsType<ProfitabilitySportMetricsRow> {
  return [
    {
      title: AppStrings.SPORT,
      dataIndex: "sport",
      key: "sport",
      width: 140,
      render: (text: string) => (
        <span className="reporting-summary-profitability-sport">{text}</span>
      ),
    },
    {
      title: AppStrings.ACCEPTED_BETS,
      dataIndex: "acceptedBets",
      key: "acceptedBets",
      align: "right",
      render: (v: number) => formatDecimal(v, 0),
    },
    {
      title: AppStrings.REPORTING_ACCEPTED_TURNOVER,
      dataIndex: "acceptedTurnover",
      key: "acceptedTurnover",
      align: "right",
      render: (v: number) => formatCurrency(v, currencySymbol, 2),
    },
    {
      title: AppStrings.REPORTING_AVG_STAKE,
      dataIndex: "avgStake",
      key: "avgStake",
      align: "right",
      render: (v: number) => formatCurrency(v, currencySymbol, 2),
    },
    {
      title: AppStrings.REPORTING_PL_COLUMN,
      dataIndex: "pl",
      key: "pl",
      align: "right",
      render: (v: number) => (
        <ReportingSummaryPlCell value={v} currencySymbol={currencySymbol} />
      ),
    },
    {
      title: AppStrings.MARGIN,
      dataIndex: "margin",
      key: "margin",
      align: "right",
      render: (v: number) => <ReportingSummaryMarginCell value={v} />,
    },
  ];
}

export function getProfitabilityTopEventsColumns(
  currencySymbol: string = "$",
): ColumnsType<ProfitabilityEventMetricsRow> {
  const metricColumns = getProfitabilitySummaryColumns(currencySymbol);
  return [
    {
      title: AppStrings.EVENT,
      dataIndex: "event",
      key: "event",
      width: 240,
    },
    {
      title: AppStrings.SPORT,
      dataIndex: "sport",
      key: "sport",
      width: 140,
    },
    {
      title: AppStrings.LEAGUE,
      dataIndex: "league",
      key: "league",
      width: 140,
    },
    ...(metricColumns as unknown as ColumnsType<ProfitabilityEventMetricsRow>),
  ];
}
